// Deep links to places that actually rate wine.
//
// We deliberately do not fetch, cache or republish anyone's scores. Critic
// ratings are copyrighted, several of these sites forbid automated collection
// in their robots.txt, and Vinmonopolet exposes no rating field at all. What we
// can do is build a precise search URL per bottle so the real rating is one
// click away, which is the same thing you would type by hand.

const VINTAGE_RE = /\b(19|20)\d{2}\b/;

// Tokens Vinmonopolet appends that only get in the way of a search elsewhere.
const NOISE = [
  /\b\d+(?:[.,]\d+)?\s*(?:cl|l|ml)\b/gi,
  /\bmagnum\b/gi,
  /\bhalvflaske\b/gi,
  /\b1\/2\s*fl\.?\b/gi,
  /\bøko(?:logisk)?\b/gi,
  /\bbiodynamisk\b/gi,
  /\(.*?\)/g,
];

// Vinmonopolet's house abbreviations. Norwegian sites use the same shorthand,
// international ones do not, so we only expand for the latter.
const ABBREV = [
  [/\bch\.\s*/gi, 'Chateau '],
  [/\bdom\.\s*/gi, 'Domaine '],
  [/\bchâteau\b/gi, 'Chateau'],
];

function tidy(value) {
  return (value || '').replace(/\s{2,}/g, ' ').replace(/[,\s]+$/, '').trim();
}

// Pull the vintage out of a Vinmonopolet product name and return the rest.
export function splitVintage(name) {
  let base = name || '';
  NOISE.forEach(re => {
    base = base.replace(re, ' ');
  });
  const match = base.match(VINTAGE_RE);
  const vintage = match ? match[0] : '';
  if (match) base = base.replace(VINTAGE_RE, ' ');
  return { base: tidy(base), vintage };
}

// "Ch. Talbot" -> "Talbot". Byklum files his tasting notes by estate, so the
// bare estate name finds every vintage he has written up rather than none.
export function estateName(base) {
  return tidy(
    (base || '')
      .replace(/^(?:ch\.|château|chateau|dom\.|domaine|clos)\s+/i, '')
      .replace(/\b(?:grand\s+cru|cru\s+class[ée]|1er|2eme|2ème)\b/gi, '')
  );
}

function international(base) {
  let value = base || '';
  ABBREV.forEach(([re, to]) => {
    value = value.replace(re, to);
  });
  return tidy(value);
}

/**
 * Build the list of rating lookups for one bottle.
 *
 * @param {string} name    Vinmonopolet product name, vintage and all.
 * @param {boolean} bordeaux  Include Christer Byklum's Bordeaux archive.
 * @returns {{id: string, label: string, url: string, note: string}[]}
 */
export function ratingLinks(name, bordeaux = false) {
  const { base, vintage } = splitVintage(name);
  if (!base) return [];

  const estate = estateName(base);
  const global = international(base);
  const withVintage = tidy(`${global} ${vintage}`);
  const links = [];

  // Norwegian, rates what Vinmonopolet actually sells, so the best first stop.
  links.push({
    id: 'aperitif',
    label: 'Aperitif',
    note: 'norwegian',
    url: `https://www.aperitif.no/pollisten?query=${encodeURIComponent(base)}`,
  });

  if (bordeaux && estate) {
    // ~17 000 tasting notes across 450+ Bordeaux estates, vintage by vintage.
    links.push({
      id: 'byklum',
      label: 'Byklum',
      note: 'bordeaux',
      url: `https://bmybordeaux.blogspot.com/search?q=${encodeURIComponent(estate)}`,
    });
  }

  // Aggregates the critics we are not allowed to copy, Parker and Byklum among
  // them, against the exact vintage.
  links.push({
    id: 'wineSearcher',
    label: 'Wine-Searcher',
    note: 'critics',
    url: `https://www.wine-searcher.com/find/${encodeURIComponent(
      withVintage.toLowerCase().replace(/\s+/g, '+')
    ).replace(/%2B/g, '+')}`,
  });

  links.push({
    id: 'vivino',
    label: 'Vivino',
    note: 'crowd',
    url: `https://www.vivino.com/search/wines?q=${encodeURIComponent(withVintage)}`,
  });

  return links;
}

export default ratingLinks;
